import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaLinkedinIn } from 'react-icons/fa';
import './Footer.css';
import { fetchFooterData } from '../services/api';

const Footer = () => {
  const [footerData, setFooterData] = useState(null);
  const strapiUrl = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';

  useEffect(() => {
    fetchFooterData().then(data => {
      if (data) {
        setFooterData(data);
      }
    });
  }, []);

  const defaultServices = [
    { name: "Land Acquisition", path: "/services/land-acquisition" },
    { name: "Leasing", path: "/services/leasing" },
    { name: "Compliance", path: "/services/compliance" },
    { name: "BTS Solutions", path: "/services/bts-solutions" },
    { name: "Pre-Leased Assets", path: "/services/pre-leased-assets" }
  ];

  const defaultQuickLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about-us" },
    { name: "Projects", path: "/projects" },
    { name: "Locations", path: "/locations" },
    { name: "Careers", path: "/careers" },
    { name: "Contact", path: "/contact" }
  ];

  const defaultBusinessHours = [
    "Monday - Friday: 9:00 AM - 6:00 PM",
    "Saturday: 10:00 AM - 4:00 PM",
    "Sunday: Closed"
  ];

  const defaultLegalLinks = [
    { name: "Privacy Policy", path: "/privacy-policy" },
    { name: "Terms & Conditions", path: "/terms-conditions" },
    { name: "Sitemap", path: "/sitemap.xml", target: "_blank" }
  ];

  const services = footerData?.services || defaultServices;
  const quickLinks = footerData?.quickLinks || defaultQuickLinks;
  const businessHours = footerData?.businessHours || defaultBusinessHours;
  const legalLinks = footerData?.legalLinks || defaultLegalLinks;

  const addressLine1 = footerData?.addressLine1 || "1st floor, Bluedart Building";
  const addressLine2 = footerData?.addressLine2 || "Old Delhi Gurgaon Road";
  const addressLine3 = footerData?.addressLine3 || "Samalkha, Delhi-110037";
  const phone = footerData?.phone || "+91 98105 54007";
  const email = footerData?.email || "info@pmgaggregators.com";
  const linkedinUrl = footerData?.linkedinUrl || "https://www.linkedin.com/company/pmg-land-leasing/";

  const footerLogoUrl = footerData?.logo?.url ? `${strapiUrl}${footerData.logo.url}` : "/WhiteLogo.png";

  return (
    <footer className="footer">
      <div className="container">
        {/* Footer Top */}
        <div className="footer-top">
          <div className="footer-brand-section">
            <Link to="/" className="footer-logo" aria-label="PMG Home">
              <img 
                src={footerLogoUrl} 
                alt="PMG Logo" 
                className="footer-logo-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/WhiteLogo.png";
                }}
              />
            </Link>
            <p className="footer-tagline">
              India's trusted partner in industrial land aggregation, compliant warehousing developments, and institutional asset leasing.
            </p>
          </div>
        </div>

        {/* Footer Main */}
        <div className="footer-main">
          {/* Quick Links */}
          <div className="footer-column">
            <h3 className="footer-title">Quick Links</h3>
            <ul className="footer-links">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.path} className="footer-link">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Services */}
          <div className="footer-column">
            <h3 className="footer-title">Our Services</h3>
            <ul className="footer-links">
              {services.map((service, index) => (
                <li key={index}>
                  <Link to={service.path} className="footer-link">
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="footer-column contact-column">
            <h3 className="footer-title">Contact Info</h3>
            <div className="contact-info">
              <div className="contact-item">
                <FaMapMarkerAlt className="contact-icon" aria-hidden="true" />
                <div className="contact-details">
                  <span className="contact-label">Corporate Office</span>
                  <p>{addressLine1}</p>
                  <p>{addressLine2}</p>
                  <p>{addressLine3}</p>
                </div>
              </div>

              <div className="contact-item">
                <FaPhoneAlt className="contact-icon" aria-hidden="true" />
                <div className="contact-details">
                  <span className="contact-label">Phone Support</span>
                  <a href={`tel:${phone.replace(/\s+/g, '')}`} className="footer-contact-link">
                    {phone}
                  </a>
                </div>
              </div>

              <div className="contact-item">
                <FaEnvelope className="contact-icon" aria-hidden="true" />
                <div className="contact-details">
                  <span className="contact-label">Email Support</span>
                  <a href={`mailto:${email}`} className="footer-contact-link">
                    {email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Connect & Hours */}
          <div className="footer-column">
            <h3 className="footer-title">Connect</h3>
            <div className="social-links">
              <a 
                href={linkedinUrl}
                className="social-text-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaLinkedinIn className="social-icon" aria-hidden="true" />
                <span>LinkedIn</span>
              </a>
            </div>
            
            <div className="business-hours">
              <div className="business-hours-title">Business Hours</div>
              {businessHours.map((hour, index) => (
                <p key={index}>{hour}</p>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="copyright">
            <p>© {new Date().getFullYear()} PMG Land Aggregators LLP. All rights reserved.</p>
          </div>
          
          <div className="footer-legal">
            {legalLinks.map((link, index) => (
              <React.Fragment key={index}>
                {link.target ? (
                  <a href={link.path} target={link.target} rel="noopener noreferrer">{link.name}</a>
                ) : (
                  <Link to={link.path}>{link.name}</Link>
                )}
                {index < legalLinks.length - 1 && <span className="divider">•</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;